import React, { useCallback, useEffect, useState } from 'react';
import { useHistory, useLocation } from 'react-router-dom';
import { useSnackbar } from 'notistack';

import { hasCalibrationAssignmentAccess } from '../../../lib/services/scoring.service';
import { useCalibrationAssignmentSummaryPoll } from '../../../lib/services/calibration-assignment.service';
import { getCurrUser } from '../../../lib/services/user.service';
import { getIsSavingScores } from '../OfflineHandler/offline-handler.container';
import { usePromptScheduler } from '../../../lib/hooks/use-prompt-scheduler';
import { STAFF_PATHS } from '../../../../_configuration';
import { isScoringSurfacePath } from './calibration-assignments-poller.helpers';
import { CalibrationPendingPrompt } from './calibration-pending-prompt.component';

// Independent cooldown bucket for this prompt-type (uses the global promptCooldownHours default).
const CALIBRATION_PROMPT_KEY = 'calibration-assignments';
// One stable snackbar key so the nudge never stacks duplicates and is closeable on action.
const PROMPT_SNACKBAR_KEY = 'calibration-assignments-prompt';

// Renderless, mounted once in the staff tree (never the parent app). Polls the pending count
// for the nav badge and fires a throttled prompt to /assignments, suppressed mid-evaluation.
export const CalibrationAssignmentsPoller: React.FC = () => {
  const { hasAccess, loading } = hasCalibrationAssignmentAccess();
  const { user } = getCurrUser();
  const userId: string | undefined = user?._id;
  const isSaving = getIsSavingScores();
  const history = useHistory();
  const { pathname } = useLocation();
  const { enqueueSnackbar, closeSnackbar } = useSnackbar();

  // Pause polling when the device is offline or the tab is hidden; resume on online/visible.
  const [online, setOnline] = useState(() => navigator.onLine);
  const [visible, setVisible] = useState(() => !document.hidden);

  useEffect(() => {
    // One AbortController signal unhooks all three listeners on unmount.
    const ac = new AbortController();
    const { signal } = ac;
    window.addEventListener('online', () => setOnline(true), { signal });
    window.addEventListener('offline', () => setOnline(false), { signal });
    document.addEventListener('visibilitychange', () => setVisible(!document.hidden), { signal });
    return () => ac.abort();
  }, []);

  const enabled = hasAccess && !loading && online && visible;
  // Definitive access loss (signed out / role revoked) — distinct from a transient
  // offline/hidden pause, which holds the last-known count instead of reading as zero.
  const accessRevoked = !loading && !hasAccess;
  // identityKey zeroes the badge on a user switch (shared device) — clearStore leaves the var set.
  const { pendingCount } = useCalibrationAssignmentSummaryPoll({ enabled, accessRevoked, identityKey: userId });

  // Suppress mid-evaluation: while a save/sync is in flight, or on any scoring surface.
  const suppressed = isSaving || isScoringSurfacePath(pathname);
  // Per-user cooldown (userId scopes the key) + per-session (sessionStorage); the scheduler
  // stays inert until the user id resolves, and sign-out clears the stamps too.
  const { canShow, markShown, snooze } = usePromptScheduler({
    promptKey: CALIBRATION_PROMPT_KEY,
    userId,
    scope: 'session',
    suppressed,
  });

  // Suppression only blocks NEW prompts; one shown on another page would linger after
  // navigating here. Dismiss it the moment the user reaches a scoring surface.
  useEffect(() => {
    if (isScoringSurfacePath(pathname)) closeSnackbar(PROMPT_SNACKBAR_KEY);
  }, [pathname, closeSnackbar]);

  // Close on unmount so the prompt never outlives the poller (sign-out, tree switch) and
  // strand a "View" action on the login/parent screen the poller no longer backs.
  useEffect(() => () => closeSnackbar(PROMPT_SNACKBAR_KEY), [closeSnackbar]);

  const handleView = useCallback(() => {
    closeSnackbar(PROMPT_SNACKBAR_KEY);
    history.push(STAFF_PATHS.ASSIGNMENTS);
  }, [closeSnackbar, history]);

  const handleRemindLater = useCallback(() => {
    closeSnackbar(PROMPT_SNACKBAR_KEY);
    snooze();
  }, [closeSnackbar, snooze]);

  // No shown/hidden mirror state: closeSnackbar is a no-op when nothing is enqueued,
  // preventDuplicate dedupes by key, and markShown flips canShow off until the cooldown ends.
  useEffect(() => {
    // Nothing to prompt: no pending items, or access is gone (revoked/signed out). Gate on
    // accessRevoked directly — the held count lags it by one layout-effect cycle.
    if (accessRevoked || pendingCount <= 0) {
      closeSnackbar(PROMPT_SNACKBAR_KEY);
      return;
    }
    if (!canShow) return;
    enqueueSnackbar(
      <CalibrationPendingPrompt onView={handleView} onRemindLater={handleRemindLater} />,
      {
        key: PROMPT_SNACKBAR_KEY,
        preventDuplicate: true,
        variant: 'info',
        // Persist until the user acts (View/Later) or the count clears — never auto-dismiss.
        persist: true,
        anchorOrigin: { vertical: 'bottom', horizontal: 'right' },
      },
    );
    markShown();
  }, [accessRevoked, pendingCount, canShow, closeSnackbar, enqueueSnackbar, handleView, handleRemindLater, markShown]);

  return null;
};
